import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, MapPin, Calendar, Clock, ChevronLeft, Wine, Download } from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import CelebrationLasers from "@/components/common/CelebrationLasers";

const BonocopaPaymentSuccess = () => {
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [bonocopas, setBonocopas] = useState([]);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const qrRefs = useRef([]);
  
  const sessionId = searchParams.get('session_id');
  const eventId = searchParams.get('event_id');
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        setError('Sesión de pago no encontrada');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/bonocopas/payment-status/${sessionId}`);
        
        if (response.data.status === 'paid') {
          setStatus('success');
          setBonocopas(response.data.bonocopas || []);
          setEvent(response.data.event);
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 2500);
        } else if (response.data.status === 'expired' || response.data.status === 'cancelled') {
          setStatus('error');
          setError('El pago fue cancelado o expiró');
        } else if (pollingCount < 5) {
          // Keep polling
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('No se pudo verificar el pago. Por favor, contacta con soporte.');
        }
      } catch (e) {
        console.error('Error checking payment:', e);
        if (pollingCount < 5) {
          setTimeout(() => setPollingCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
          setError('Error al verificar el estado del pago');
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, eventId, pollingCount]);
  
  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <CelebrationLasers show={showCelebration} />
      <div className="main-container relative z-10">
        <div className="text-center mb-6 fade-in-up">
          <h1 className="font-logo text-3xl md:text-4xl text-white neon-glow">
            TAYTANTOS
          </h1>
        </div>
        
        {status === 'checking' && (
          <div className="form-section text-center fade-in-up">
            <div className="spinner mx-auto mb-4"></div>
            <h2 className="text-white text-xl font-bold mb-2">Verificando pago...</h2>
            <p className="text-[var(--text-muted)]">Por favor, espera mientras confirmamos tu compra.</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="form-section text-center fade-in-up">
            <div className="text-red-500 text-6xl mb-4">✕</div>
            <h2 className="text-white text-xl font-bold mb-2">Error en el pago</h2>
            <p className="text-[var(--text-muted)] mb-4">{error}</p>
            <button
              onClick={() => navigate(eventId ? `/events/${eventId}` : '/')}
              className="btn-secondary"
            >
              <ChevronLeft size={18} />
              Volver
            </button>
          </div>
        )}
        
        {status === 'success' && bonocopas.length > 0 && (
          <div className="form-section fade-in-up">
            <div className="text-center mb-6">
              <CheckCircle size={48} className="text-[var(--success)] mx-auto mb-4" />
              <h2 className="text-white text-2xl font-bold mb-2">
                ¡Bonocopa{bonocopas.length > 1 ? 's' : ''} Confirmado{bonocopas.length > 1 ? 's' : ''}!
              </h2>
              <p className="text-[var(--text-muted)]">
                Te hemos enviado un email con tus códigos de bonocopa.
              </p>
            </div>
            
            {/* Event Info */}
            {event && (
              <div className="bg-[var(--surface)] p-4 mb-6 space-y-2">
                <h3 className="text-white font-bold text-lg">{event.nombre}</h3>
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                  <MapPin size={14} />
                  <span>{event.sala}</span>
                </div>
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                  <Calendar size={14} />
                  <span>{event.fecha}</span>
                </div>
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                  <Clock size={14} />
                  <span>{event.hora}</span>
                </div>
              </div>
            )}
            
            {/* Bonocopas List with QR */}
            <div className="space-y-6">
              <h3 className="text-yellow-500 font-bold uppercase text-center text-xl">🍸 Tus Bonocopas 🍸</h3>
              
              {bonocopas.map((bc, index) => (
                <div 
                  key={bc.id}
                  ref={el => qrRefs.current[index] = el}
                  className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/5 border-2 border-yellow-500 p-6 text-center"
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Wine size={28} className="text-yellow-500" />
                    <span className="text-white font-bold text-xl">BONOCOPA #{index + 1}</span>
                    <Wine size={28} className="text-yellow-500" />
                  </div>
                  
                  {/* QR Code */}
                  <div className="bg-white p-4 rounded-lg inline-block mb-4">
                    <QRCodeSVG 
                      value={bc.codigo}
                      size={150}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  
                  {/* Codigo */}
                  <div className="bg-black/50 p-3 my-3 border border-yellow-500/30">
                    <p className="text-yellow-500 font-mono text-2xl font-bold tracking-widest">
                      {bc.codigo}
                    </p>
                  </div>
                  
                  {/* Copas info */}
                  <div className="bg-yellow-500/10 p-3 rounded mt-4">
                    <p className="text-yellow-400 text-lg font-bold">
                      🍹 {bc.copas_totales} COPAS INCLUIDAS 🍹
                    </p>
                    <p className="text-red-400 text-xs mt-2">
                      *No incluye bebidas premium ni champagne
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Instructions */}
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded">
              <p className="text-green-400 text-sm text-center font-medium">
                ✅ Presenta el QR en la barra del evento para canjear tus copas.<br/>
                Cada código es único y se descuentan las copas consumidas.
              </p>
            </div>
            
            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="btn-secondary w-full mt-6"
            >
              <ChevronLeft size={18} />
              Volver al inicio
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BonocopaPaymentSuccess;
